import React, { forwardRef } from 'react';
import { numberToWords } from '../utils/formatCurrency';
import { LOGO_DATA_URL } from '../utils/logoData';
import { QR_CODE_DATA_URL } from '../utils/qrCodeData';

const fmt = (n) => '\u20B9' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};

const LabelValue = ({ label, value }) => (
  <div style={{ fontSize: '9px', color: '#555', lineHeight: 1.6 }}>
    <span style={{ fontWeight: 700, color: '#000' }}>{label}</span> {value}
  </div>
);

const thStyle = {
  background: '#f5f5f5', fontWeight: 700, fontSize: '8px',
  padding: '3mm 3mm', borderBottom: '2px solid #ddd', textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = { padding: '3mm 3mm', borderBottom: '1px solid #eee', fontSize: '9px' };

const QuotationPreview = forwardRef(({ form, subtotal, discount = 0, totalGst, grandTotal, itemSubtotal, itemGst, itemTotal }, ref) => {
  const cin = (form.companyCIN || '').replace(/^CIN:\s*/i, '');
  const gstin = (form.companyGSTIN || '').replace(/^GSTIN:\s*/i, '');
  const hasSchedule = form.paymentSchedule && form.paymentSchedule.length > 0;

  return (
    <div ref={ref} style={{
      width: '210mm', minHeight: '297mm', margin: '0 auto', padding: '15mm',
      fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: '10px',
      color: '#000', background: '#fff', boxSizing: 'border-box', lineHeight: 1.5,
    }}>

      {/* ── Logo ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6mm' }}>
        <img src={LOGO_DATA_URL} alt="Logo" style={{ width: '55mm', height: 'auto' }} />
      </div>

      {/* ── Company details + Quotation meta ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6mm' }}>
        <div style={{ maxWidth: '55%' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '2mm' }}>{form.companyName}</div>
          {form.companyAddress && (
            <div style={{ fontSize: '9px', color: '#555', lineHeight: 1.6 }}>
              <span style={{ fontWeight: 700, color: '#000' }}>Head Office:</span><br />
              {form.companyAddress.split('\n').map((l, i) => <span key={i}>{l}<br /></span>)}
            </div>
          )}
          {form.companyPhone && <LabelValue label="Ph:" value={form.companyPhone} />}
          {form.companyEmail && <LabelValue label="Email:" value={form.companyEmail} />}
          {form.companyWebsite && <LabelValue label="Website:" value={form.companyWebsite} />}
          {cin && <LabelValue label="CIN:" value={cin} />}
          {gstin && <LabelValue label="GSTIN:" value={gstin} />}
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '2mm' }}># {form.quotationNumber}</div>
          <div style={{ marginTop: '2mm' }}>
            <div style={{ fontSize: '9px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Estimated Total</div>
            <div style={{ fontSize: '22px', fontWeight: 700, marginTop: '1mm' }}>{fmt(grandTotal)}</div>
          </div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid #ddd', margin: '4mm 0' }} />

      {/* ── To + Dates ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6mm' }}>
        <div style={{ maxWidth: '50%' }}>
          <div style={{ fontSize: '8px', color: '#888', textTransform: 'uppercase', marginBottom: '1mm' }}>To</div>
          {form.customerTo && form.customerTo.split('\n').map((l, i) => (
            <div key={i} style={i === 0 ? { fontSize: '13px', fontWeight: 700 } : { fontSize: '10px', color: '#333' }}>{l}</div>
          ))}
        </div>
        <table style={{ fontSize: '9px', borderCollapse: 'collapse' }}>
          <tbody>
            {[
              ['Quotation Date :', fmtDate(form.quotationDate)],
              ['Valid Until :', fmtDate(form.validUntil)],
            ].map(([lbl, val], i) => (
              <tr key={i}>
                <td style={{ padding: '1.5mm 6mm 1.5mm 0', color: '#888' }}>{lbl}</td>
                <td style={{ padding: '1.5mm 0', textAlign: 'right', fontWeight: 500 }}>{val}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Items Table ── */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2mm', fontSize: '9px' }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: '7mm', textAlign: 'center' }}>#</th>
            <th style={{ ...thStyle, textAlign: 'left' }}>Description</th>
            <th style={{ ...thStyle, width: '14mm', textAlign: 'right' }}>Qty</th>
            <th style={{ ...thStyle, width: '22mm', textAlign: 'right' }}>Rate</th>
            <th style={{ ...thStyle, width: '14mm', textAlign: 'right' }}>GST %</th>
            <th style={{ ...thStyle, width: '28mm', textAlign: 'right' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((item, i) => {
            const gstPct = parseFloat(item.gst) || 0;
            const total = itemTotal(item);
            return (
              <tr key={i} style={i % 2 === 0 ? { background: '#fafafa' } : {}}>
                <td style={{ ...tdStyle, textAlign: 'center', color: '#888' }}>{i + 1}</td>
                <td style={tdStyle}>{item.description || ''}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{parseFloat(item.qty || 0).toFixed(2)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(item.rate)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{gstPct > 0 ? `${gstPct}%` : '-'}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmt(total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ── Totals ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6mm' }}>
        <table style={{ fontSize: '10px', borderCollapse: 'collapse', minWidth: '65mm' }}>
          <tbody>
            <tr>
              <td style={{ padding: '2mm 8mm 2mm 0', color: '#555' }}>Subtotal</td>
              <td style={{ padding: '2mm 0', textAlign: 'right' }}>{fmt(subtotal)}</td>
            </tr>
            {discount > 0 && (
              <tr>
                <td style={{ padding: '2mm 8mm 2mm 0', color: '#555' }}>Discount</td>
                <td style={{ padding: '2mm 0', textAlign: 'right' }}>-{fmt(discount)}</td>
              </tr>
            )}
            {totalGst > 0 && (
              <tr>
                <td style={{ padding: '2mm 8mm 2mm 0', color: '#555' }}>GST (18%)</td>
                <td style={{ padding: '2mm 0', textAlign: 'right' }}>{fmt(totalGst)}</td>
              </tr>
            )}
            <tr>
              <td style={{ padding: '2.5mm 8mm 2.5mm 0', fontWeight: 700, borderTop: '2px solid #000', fontSize: '11px' }}>Total</td>
              <td style={{ padding: '2.5mm 0', textAlign: 'right', fontWeight: 700, borderTop: '2px solid #000', fontSize: '11px' }}>{fmt(grandTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Total in words */}
      <div style={{ textAlign: 'right', marginBottom: '6mm' }}>
        <span style={{ fontSize: '8px', color: '#888' }}>Total In Words: </span>
        <span style={{ fontSize: '9px', fontWeight: 700 }}>{numberToWords(grandTotal)}</span>
      </div>

      {/* ── Payment Schedule ── */}
      {hasSchedule && (
        <div style={{ borderTop: '1px solid #ddd', paddingTop: '3mm', marginBottom: '4mm' }}>
          <div style={{ fontSize: '9px', fontWeight: 700, marginBottom: '2mm' }}>Payment Schedule</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5px' }}>
            <thead>
              <tr>
                <th style={{ padding: '1.5mm 2.5mm', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: 700, fontSize: '7.5px', color: '#555' }}>Installment</th>
                <th style={{ padding: '1.5mm 2.5mm', textAlign: 'right', borderBottom: '1px solid #ddd', fontWeight: 700, fontSize: '7.5px', color: '#555' }}>%</th>
                <th style={{ padding: '1.5mm 2.5mm', textAlign: 'right', borderBottom: '1px solid #ddd', fontWeight: 700, fontSize: '7.5px', color: '#555' }}>Amount</th>
                <th style={{ padding: '1.5mm 2.5mm', textAlign: 'right', borderBottom: '1px solid #ddd', fontWeight: 700, fontSize: '7.5px', color: '#555' }}>Due Date</th>
              </tr>
            </thead>
            <tbody>
              {form.paymentSchedule.map((ps, i) => {
                const pct = parseFloat(ps.percent) || 0;
                return (
                  <tr key={i} style={i % 2 === 0 ? { background: '#fafafa' } : {}}>
                    <td style={{ padding: '1.5mm 2.5mm', borderBottom: '1px solid #eee' }}>{ps.label || `Installment ${i + 1}`}</td>
                    <td style={{ padding: '1.5mm 2.5mm', borderBottom: '1px solid #eee', textAlign: 'right' }}>{pct}%</td>
                    <td style={{ padding: '1.5mm 2.5mm', borderBottom: '1px solid #eee', textAlign: 'right', fontWeight: 600 }}>{fmt(grandTotal * pct / 100)}</td>
                    <td style={{ padding: '1.5mm 2.5mm', borderBottom: '1px solid #eee', textAlign: 'right' }}>{fmtDate(ps.dueDate)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Notes ── */}
      {form.notes && (
        <div style={{ borderTop: '1px solid #ddd', paddingTop: '4mm', marginBottom: '6mm' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, marginBottom: '2mm' }}>Notes</div>
          <div style={{ fontSize: '9px', color: '#555', whiteSpace: 'pre-line' }}>{form.notes}</div>
        </div>
      )}

      {/* ── Payment Options ── */}
      {(form.accountName || form.accountNumber || form.bankName || form.ifsc) && (
        <div style={{ borderTop: '1px solid #ddd', paddingTop: '3mm', marginBottom: '4mm' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6mm' }}>
            <div>
              <div style={{ fontSize: '9px', fontWeight: 700, marginBottom: '2mm' }}>Account Details</div>
              <table style={{ fontSize: '8.5px', borderCollapse: 'collapse' }}>
                <tbody>
                  {[
                    form.accountName && ['Account Name', form.accountName],
                    form.accountNumber && ['Account No', form.accountNumber],
                    form.bankName && ['Bank', form.bankName],
                    form.ifsc && ['IFSC', form.ifsc],
                    form.branch && ['Branch', form.branch],
                  ].filter(Boolean).map(([lbl, val], i) => (
                    <tr key={i}>
                      <td style={{ padding: '0.8mm 5mm 0.8mm 0', fontWeight: 700, color: '#000', whiteSpace: 'nowrap' }}>{lbl}:</td>
                      <td style={{ padding: '0.8mm 0', color: '#555' }}>{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ textAlign: 'center' }}>
              <img src={QR_CODE_DATA_URL} alt="UPI QR Code" style={{ width: '28mm', height: 'auto' }} />
              <div style={{ fontSize: '7px', color: '#888', marginTop: '1mm' }}>Scan to pay via UPI</div>
            </div>
          </div>
          <div style={{ fontSize: '7.5px', color: '#888', marginTop: '2mm', fontStyle: 'italic' }}>
            Either the account details or the QR code above can be used to make payment.
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <div style={{ borderTop: '1px solid #eee', marginTop: '10mm', paddingTop: '3mm', fontSize: '8px', color: '#aaa' }}>
        <div style={{ marginBottom: '1mm' }}>This quotation is valid until {fmtDate(form.validUntil)}. Prices are subject to change after the validity period.</div>
        <div>This is not a tax invoice. A tax invoice will be issued upon confirmation of the order.</div>
      </div>
    </div>
  );
});

QuotationPreview.displayName = 'QuotationPreview';
export default QuotationPreview;
